import type { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { Variants as TagVariant } from '@ds/components/Tag'
import type { Location } from '@ds/types/Location'

import { API_TAG_LABEL_CLASS_TO_TAG_VARIANT } from '@/scopes/care-commons/components/order/OrderlineCard/OrderlineCard.constants'
import type { Orderline } from '~/api/my-orders/types/OrderlineList.types'
import { ORDERLINE_TIMELINE_STEP_TYPE } from '~/api/my-orders/types/OrderlineTimeline.types'

import translations from '../OrdersSection.translations'
import type { SaleCard } from '../OrdersSection.types'
import { WEEKDAY_DATE_FORMAT, compareByDateDesc } from '../OrdersSection.utils'

type OrderlineWithLabel = Orderline & { state: { label: string } }

/** Everything the card builders need from the Nuxt setup context. */
export type SaleCardContext = {
  i18n: ReturnType<typeof useI18n>
  ordersPageTo: Location
}

type SaleCardGroup = {
  sortDate: Date
  cards: SaleCard[]
}

/**
 * Builds the sale cards: one per orderline, where each replaced orderline
 * is grouped with its replacement(s) — original first, replacements right
 * after it — and groups are sorted newest first.
 */
export function toSaleCards(
  results: Orderline[] | undefined,
  ctx: SaleCardContext,
): SaleCard[] {
  const labelled = (results ?? []).filter(hasStateLabel)

  return buildReplacementGroups(labelled, ctx)
    .sort(compareByDateDesc)
    .flatMap((group) => group.cards)
}

// Only keep orderlines with a human-readable state label — state.label is
// null for UNKNOWN / unmapped statuses.
function hasStateLabel(orderline: Orderline): orderline is OrderlineWithLabel {
  return orderline.state.label !== null
}

/**
 * Groups each replaced orderline with its replacement(s), sorted on the
 * original's date so the group stays adjacent once merged with trade-ins.
 */
function buildReplacementGroups(
  results: OrderlineWithLabel[],
  ctx: SaleCardContext,
): SaleCardGroup[] {
  const orderlinesByPublicId = new Map(
    results.map((orderline) => [orderline.publicId, orderline] as const),
  )
  const replacedPublicIds = new Set(
    results.flatMap((orderline) => getReplacementPublicIds(orderline)),
  )
  // Every orderline already rendered — guards against duplicate links and
  // cycles in the replacement chain.
  const visited = new Set<string>()

  const groups: SaleCardGroup[] = []

  for (const original of results) {
    // Skip orderlines that are themselves a replacement — they are rendered
    // inside their original's group.
    if (replacedPublicIds.has(original.publicId)) continue

    const cards = collectChainCards(
      original,
      ctx,
      orderlinesByPublicId,
      visited,
    )

    groups.push({ sortDate: cards[0].sortDate, cards })
  }

  // Corrupt data fallback (e.g. a replacement cycle): every fetched
  // orderline must be rendered, so group the leftovers on their own.
  for (const leftover of results) {
    if (visited.has(leftover.publicId)) continue

    const cards = collectChainCards(
      leftover,
      ctx,
      orderlinesByPublicId,
      visited,
    )

    groups.push({ sortDate: cards[0].sortDate, cards })
  }

  return groups
}

/**
 * Cards of a replacement chain, collected depth-first from its root: the
 * orderline itself first, then each replacement — and, for chained
 * replacements, the replacement's own replacements, and so on.
 */
function collectChainCards(
  orderline: OrderlineWithLabel,
  ctx: SaleCardContext,
  orderlinesByPublicId: Map<string, OrderlineWithLabel>,
  visited: Set<string>,
  sortDateOverride?: Date,
): SaleCard[] {
  visited.add(orderline.publicId)

  const cards = [toSaleCard(orderline, ctx, sortDateOverride)]

  for (const publicId of getReplacementPublicIds(orderline)) {
    const replacement = orderlinesByPublicId.get(publicId)

    if (replacement && !visited.has(publicId)) {
      cards.push(
        ...collectChainCards(
          replacement,
          ctx,
          orderlinesByPublicId,
          visited,
          // Replacements sort on their original's date so the group stays
          // adjacent.
          sortDateOverride ?? new Date(orderline.creationDate),
        ),
      )
    }
  }

  return cards
}

function getReplacementPublicIds(orderline: OrderlineWithLabel): string[] {
  const publicIds: string[] = []

  if (orderline.replacedByReplacementOrderline) {
    publicIds.push(orderline.replacedByReplacementOrderline.publicId)
  }

  if (orderline.expressReplacedByReplacementOrderline) {
    publicIds.push(orderline.expressReplacedByReplacementOrderline.publicId)
  }

  return publicIds
}

function toSaleCard(
  orderline: OrderlineWithLabel,
  { i18n, ordersPageTo }: SaleCardContext,
  // Replacements sort on their original's date so the group stays adjacent.
  sortDateOverride?: Date,
): SaleCard {
  return {
    key: `sale-${orderline.publicId}`,
    type: 'sale',
    sortDate: sortDateOverride ?? new Date(orderline.creationDate),
    cardProps: {
      tagLabel: orderline.state.label,
      tagVariant: toTagVariant(orderline.state.labelClass),
      dateLabel: getDateLabel(orderline, i18n),
      imageUrl: orderline.product.image,
      imageAlt: orderline.product.title,
      title: orderline.product.title,
      subtitle: orderline.tag?.label ?? undefined,
      to: ordersPageTo,
    },
  }
}

const SHORT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
}

/**
 * Date line shown on a card, e.g. "On: Friday, Jul. 31".
 */
function getDateLabel(
  orderline: Orderline,
  i18n: SaleCardContext['i18n'],
): string | undefined {
  const { enumState, date } = orderline.state

  // PENDING_PAYMENT: "Payment due by: Friday, Jul. 31"
  if (enumState === 'PENDING_PAYMENT') {
    if (!date) return undefined

    return i18n(translations.datePrefixPaymentDueBy, {
      date: i18n.date(new Date(date), WEEKDAY_DATE_FORMAT),
    })
  }

  // PAID / VALIDATED / SHIPPED: "Estimated delivery: Jul. 27-29"
  // Source: the delivery timeline step's dateRange (or single date as fallback)
  if (
    enumState === 'PAID' ||
    enumState === 'VALIDATED' ||
    enumState === 'SHIPPED'
  ) {
    const deliveryStep = orderline.timeline?.find(
      (s) => s.type === ORDERLINE_TIMELINE_STEP_TYPE.delivery,
    )

    if (deliveryStep?.dateRange) {
      const min = i18n.date(
        new Date(deliveryStep.dateRange.minDate),
        SHORT_DATE_FORMAT,
      )
      const max = i18n.date(new Date(deliveryStep.dateRange.maxDate), {
        day: 'numeric',
      })

      return i18n(translations.datePrefixEstimatedDelivery, {
        date: `${min}-${max}`,
      })
    }

    if (deliveryStep?.date) {
      return i18n(translations.datePrefixEstimatedDelivery, {
        date: i18n.date(new Date(deliveryStep.date), SHORT_DATE_FORMAT),
      })
    }

    return undefined
  }

  // All other statuses with a point-in-time date: "On: Friday, Jul. 31"
  // Covers: DELIVERED, CANCELLED, REFUNDED, NOT_PAID, AWAITING_CONFIRMATION,
  //         REPLACED, EXPRESS_REPLACE_COMPLETE, EXPRESS_REPLACE_ONGOING
  if (date) {
    return i18n(translations.datePrefixOn, {
      date: i18n.date(new Date(date), WEEKDAY_DATE_FORMAT),
    })
  }

  return undefined
}

// state.labelClass is 'DEFAULT' | 'SUCCESS' | 'DANGER' | 'WARNING' | null
// (no 'INFO' — that only appears on tag.labelClass)
function toTagVariant(
  labelClass: Orderline['state']['labelClass'],
): TagVariant {
  return (API_TAG_LABEL_CLASS_TO_TAG_VARIANT[labelClass ?? 'DEFAULT'] ??
    'secondary') as TagVariant
}
